/**
 * useSupportQueue Hook
 *
 * React hook for managing support queue operations.
 * Handles queue fetching, filtering, search, and actions.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-149 - Support Dashboard (26 SP)
 *
 * Bug Fix: Use apiClient instead of raw axios for authentication
 */

import { useState, useEffect, useCallback } from 'react';
import apiRequest from '../../services/api';

const API_BASE = '/api/v1/support/queue';

/**
 * Main support queue hook
 */
export function useSupportQueue(filters = {}) {
  const [queue, setQueue] = useState({ items: [], pagination: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.status) {params.append('status', filters.status);}
      if (filters.priority) {params.append('priority', filters.priority);}
      if (filters.assigned_to) {params.append('assigned_to', filters.assigned_to);}
      if (filters.limit) {params.append('limit', filters.limit);}
      if (filters.offset) {params.append('offset', filters.offset);}

      const queryString = params.toString();
      const url = `${API_BASE}${queryString ? `?${queryString}` : ''}`;

      const response = await apiRequest(url, { method: 'GET' });

      if (response.success) {
        setQueue(response.data);
      } else {
        throw new Error('Failed to fetch support queue');
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to fetch support queue');
      }
    } finally {
      setLoading(false);
    }
  }, [filters.status, filters.priority, filters.assigned_to, filters.limit, filters.offset]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  return {
    queue,
    loading,
    error,
    refresh: fetchQueue,
  };
}

/**
 * Hook for fetching single support request
 */
export function useSupportRequest(requestId) {
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchRequest = useCallback(async () => {
    if (!requestId) {return;}

    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/${requestId}`, { method: 'GET' });

      if (response.success) {
        setRequest(response.data);
      } else {
        throw new Error('Failed to fetch support request');
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to fetch support request');
      }
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  useEffect(() => {
    fetchRequest();
  }, [fetchRequest]);

  return {
    request,
    loading,
    error,
    refresh: fetchRequest,
  };
}

/**
 * Hook for support queue statistics
 */
export function useQueueStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/stats`, { method: 'GET' });

      if (response.success) {
        setStats(response.data);
      } else {
        throw new Error('Failed to fetch queue stats');
      }
    } catch (err) {
      if (err.status === 401) {
        setError('Session expired. Please log in again.');
      } else {
        setError(err.message || 'Failed to fetch queue stats');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  return {
    stats,
    loading,
    error,
    refresh: fetchStats,
  };
}

/**
 * Hook for creating support requests
 */
export function useCreateSupportRequest() {
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);

  const create = async (data) => {
    try {
      setCreating(true);
      setError(null);

      const response = await apiRequest(API_BASE, {
        method: 'POST',
        body: JSON.stringify(data),
      });

      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to create support request');
      }
    } catch (err) {
      const errorMsg = err.status === 401 ? 'Session expired. Please log in again.' : (err.message || 'Failed to create support request');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setCreating(false);
    }
  };

  return {
    create,
    creating,
    error,
  };
}

/**
 * Hook for assigning support requests
 */
export function useAssignRequest() {
  const [assigning, setAssigning] = useState(false);
  const [error, setError] = useState(null);

  const assign = async (requestId, assignedTo) => {
    try {
      setAssigning(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/${requestId}/assign`, {
        method: 'PATCH',
        body: JSON.stringify({ assigned_to: assignedTo }),
      });

      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to assign request');
      }
    } catch (err) {
      const errorMsg = err.status === 401 ? 'Session expired. Please log in again.' : (err.message || 'Failed to assign request');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setAssigning(false);
    }
  };

  return {
    assign,
    assigning,
    error,
  };
}

/**
 * Hook for updating support request status
 */
export function useUpdateStatus() {
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);

  const updateStatus = async (requestId, status) => {
    try {
      setUpdating(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/${requestId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status }),
      });

      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      const errorMsg = err.status === 401 ? 'Session expired. Please log in again.' : (err.message || 'Failed to update status');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setUpdating(false);
    }
  };

  return {
    updateStatus,
    updating,
    error,
  };
}

/**
 * Hook for adding notes to support requests
 */
export function useAddNote() {
  const [adding, setAdding] = useState(false);
  const [error, setError] = useState(null);

  const addNote = async (requestId, note) => {
    try {
      setAdding(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/${requestId}/notes`, {
        method: 'POST',
        body: JSON.stringify({ note }),
      });

      if (response.success) {
        return response.data;
      } else {
        throw new Error('Failed to add note');
      }
    } catch (err) {
      const errorMsg = err.status === 401 ? 'Session expired. Please log in again.' : (err.message || 'Failed to add note');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setAdding(false);
    }
  };

  return {
    addNote,
    adding,
    error,
  };
}

/**
 * Hook for searching users
 */
export function useSearchUsers() {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async (criteria) => {
    try {
      setSearching(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/search/users`, {
        method: 'POST',
        body: JSON.stringify(criteria),
      });

      if (response.success) {
        setResults(response.data);
        return response.data;
      } else {
        throw new Error('Failed to search users');
      }
    } catch (err) {
      const errorMsg = err.status === 401 ? 'Session expired. Please log in again.' : (err.message || 'Failed to search users');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setSearching(false);
    }
  };

  return {
    results,
    search,
    searching,
    error,
  };
}

/**
 * Hook for searching bookings
 */
export function useSearchBookings() {
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  const search = async (criteria) => {
    try {
      setSearching(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/search/bookings`, {
        method: 'POST',
        body: JSON.stringify(criteria),
      });

      if (response.success) {
        setResults(response.data);
        return response.data;
      } else {
        throw new Error('Failed to search bookings');
      }
    } catch (err) {
      const errorMsg = err.status === 401 ? 'Session expired. Please log in again.' : (err.message || 'Failed to search bookings');
      setError(errorMsg);
      throw new Error(errorMsg);
    } finally {
      setSearching(false);
    }
  };

  return {
    results,
    search,
    searching,
    error,
  };
}

export default useSupportQueue;
